import { useEffect, useRef } from 'react';
import './App.css';

const STAR_PATH =
  'M12 2 14.25 8.1 20.66 7 16.5 12 20.66 17 14.25 15.9 12 22 9.75 15.9 3.34 17 7.5 12 3.34 7 9.75 8.1Z';

const BLOBS = [
  { h: '#8FA982', r: 0.52, x: 0.22, y: 0.3, sx: 0.00011, sy: 0.00016, px: 0.0, py: 2.1 },
  { h: '#3E5238', r: 0.6, x: 0.8, y: 0.65, sx: 0.00009, sy: 0.00013, px: 1.3, py: 4.2 },
  { h: '#C9D8BC', r: 0.34, x: 0.62, y: 0.18, sx: 0.00014, sy: 0.0001, px: 3.1, py: 0.7 },
  { h: '#B58A84', r: 0.3, x: 0.35, y: 0.85, sx: 0.00012, sy: 0.00015, px: 5.0, py: 2.9 },
  { h: '#22302A', r: 0.55, x: 0.5, y: 0.52, sx: 0.00007, sy: 0.00011, px: 4.4, py: 5.6 }
];

function Star({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d={STAR_PATH} />
    </svg>
  );
}

/** Drifting sage blobs on a low-res canvas; the CSS blur upscales it into silk. */
function AmbientSky() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf = 0;
    let W = 0;
    let H = 0;

    const size = () => {
      W = canvas.width = 420;
      H = canvas.height = Math.round(420 * (window.innerHeight / window.innerWidth)) || 420;
    };
    size();
    window.addEventListener('resize', size);

    const frame = (t: number) => {
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = '#0A0E0B';
      ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'lighter';
      BLOBS.forEach((b, i) => {
        const x = (b.x + 0.16 * Math.sin(t * b.sx + b.px)) * W;
        const y = (b.y + 0.16 * Math.cos(t * b.sy + b.py)) * H;
        const r = b.r * Math.max(W, H) * (1 + 0.08 * Math.sin(t * 0.0002 + i));
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, `${b.h}38`);
        g.addColorStop(1, `${b.h}00`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, 6.2832);
        ctx.fill();
      });
      if (!reduced) raf = window.requestAnimationFrame(frame);
    };

    if (reduced) {
      frame(40000);
    } else {
      raf = window.requestAnimationFrame(frame);
    }

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', size);
    };
  }, []);

  return (
    <div className="sky">
      <canvas ref={canvasRef} />
    </div>
  );
}

interface AppCardProps {
  variant: string;
  title: string;
  blurb: string;
  glyph: JSX.Element;
}

function AppCard({ variant, title, blurb, glyph }: AppCardProps) {
  const onPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  return (
    <article className={`card ${variant} reveal`} onPointerMove={onPointerMove}>
      <div className="glow" />
      <div className="card-top">
        <div className="glyph" aria-hidden="true">
          {glyph}
        </div>
        <span className="tag">Coming soon</span>
      </div>
      <div>
        <h2>{title}</h2>
        <p>{blurb}</p>
      </div>
    </article>
  );
}

const BARREL_GLYPH = (
  <svg viewBox="0 0 52 52" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="26" cy="26" r="22" />
    <circle cx="26" cy="26" r="15" />
    <line x1="26" y1="4" x2="26" y2="11" />
    <line x1="26" y1="41" x2="26" y2="48" />
    <line x1="4" y1="26" x2="11" y2="26" />
    <line x1="41" y1="26" x2="48" y2="26" />
    <circle cx="26" cy="26" r="3" fill="currentColor" stroke="none" />
  </svg>
);

const HOUSE_GLYPH = (
  <svg
    viewBox="0 0 52 52"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinejoin="round"
    strokeLinecap="round"
  >
    <path d="M8 24 26 8l18 16" />
    <path d="M12 22v22h28V22" />
    <path d="M21 44V32h10v12" />
  </svg>
);

function App() {
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.18 }
    );
    document.querySelectorAll('.reveal').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <AmbientSky />
      <div className="grain" />

      <header>
        <div className="brand">
          <Star className="star" />
          Good On Me
        </div>
        <div className="made">Made in Chicago</div>
      </header>

      <main>
        <section className="hero frame">
          <h1 className="wordmark" aria-label="Good On Me">
            <span className="row" aria-hidden="true">
              <span>Good</span>
            </span>
            <span className="row" aria-hidden="true">
              <span>On</span>
            </span>
            <span className="row accent" aria-hidden="true">
              <span>Me</span>
            </span>
          </h1>
          <div className="hero-foot">
            <span>App development company</span>
            <a href="#apps">Coming soon</a>
          </div>
        </section>

        <section className="apps frame" id="apps">
          <div className="eyebrow">Coming soon</div>
          <div className="grid">
            <AppCard
              variant="bp"
              title="Barrel Proof"
              blurb="Whiskey discovery and collection, from the shelf to the pour."
              glyph={BARREL_GLYPH}
            />
            <AppCard
              variant="hm"
              title="Hohm"
              blurb="Rental management for landlords and tenants, in one place."
              glyph={HOUSE_GLYPH}
            />
          </div>
        </section>

        <footer>
          <div className="frame">
            <span>&copy; 2026 Good On Me LLC</span>
            <span className="foot-right">
              <a href="mailto:nate@goodonme.io">nate@goodonme.io</a>
              <span className="made">
                Made in Chicago
                <Star className="star" />
              </span>
            </span>
          </div>
        </footer>
      </main>
    </>
  );
}

export default App;
